import {
    createReapproCategory,
    updateReapproCategory,
    useProducts,
    useReapproCategories,
    useReapproCategory,
} from '@gozoki/api';
import {
    ReapproCategoryForm,
    useReapproCategoryForm,
} from '@gozoki/api/src/forms/useReapproCategoryForm';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Button, IconButton, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useController } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingPaper from '../../components/containers/LoadingPaper';
import SelectMultipleRhf from '../../components/inputs/SelectMultipleRhf';
import TextFieldRhf from '../../components/inputs/TextFieldRhf';
import { TimePickerRhf } from '../../components/inputs/TimePickerRhf';
import Page from '../../components/Page';
import { ROUTES } from '../../utils/constants/routes.constants';
import { useRights } from '../../utils/hooks';
import { useProductSelectOptions } from '../../utils/hooks/useProductSelectOptions';
import { ThemeColors } from '../../utils/theme/colors.theme';
import { fonts } from '../../utils/theme/fonts.theme';
import { ButtonOverrides } from '../../utils/theme/overrides.theme';

export const AddReapproCategoryPage = () => {
    const navigate = useNavigate();
    const isUserAdmin = useRights(['ADMIN']);
    const queryClient = useQueryClient();

    const { id } = useParams();
    const updateMode = id !== undefined; // Update mode if there is a category ID in the URL

    const idNumber = parseInt(id ?? '', 10);

    // If this is update mode, use isLoading to show a loading indicator before the data arrives
    const { data, isLoading } = useReapproCategory(idNumber);
    const { data: reapproCategories } = useReapproCategories();
    const { data: products } = useProducts();

    const { handleSubmit, control, trigger, setValue } = useReapproCategoryForm({
        productIds: [],
    });
    const productOptions = useProductSelectOptions();

    const {
        field: { value: watchedProductIds },
    } = useController({
        control,
        name: 'productIds',
    });

    const conflictingProducts = useMemo(() => {
        const selectedProductIds = watchedProductIds ?? [];
        if (!products || selectedProductIds.length === 0) return [];

        return products
            .filter((product) => selectedProductIds.includes(product.id))
            .filter((product) => {
                if (!product.reapproCategoryId) return false;
                if (!updateMode) return true;
                return product.reapproCategoryId !== idNumber;
            })
            .map((product) => ({
                productLabel: product.label,
                categoryLabel:
                    reapproCategories?.find((category) => category.id === product.reapproCategoryId)
                        ?.name ?? 'Catégorie inconnue',
            }));
    }, [products, watchedProductIds, updateMode, idNumber, reapproCategories]);

    // Fill the form with initial data once the data is fetched
    useEffect(() => {
        if (data) {
            setValue('name', data.name);
            setValue('daysBefore', data.daysBefore);
            setValue('computeHour', data.computeHour.slice(0, 5));
            setValue('sendHour', data.sendHour.slice(0, 5));
            setValue('logHour', data.logHour.slice(0, 5));
            setValue('productIds', data.products);
        }
    }, [setValue, data]);

    const navigateBack = () => {
        queryClient.invalidateQueries({ queryKey: ['products'] });
        navigate(ROUTES.CATEGORIES, { state: { currentTab: 2 } });
    };

    const { mutate: upload, isPending: isUploading } = useMutation({
        mutationFn: createReapproCategory,
        onSuccess: navigateBack,
    });

    const { mutate: update, isPending: isUpdating } = useMutation({
        mutationFn: (newData: ReapproCategoryForm) => updateReapproCategory(idNumber, newData),
        onSuccess: navigateBack,
    });

    return (
        <Page sx={{ maxWidth: '50vw', margin: 'auto' }}>
            <form onSubmit={handleSubmit(updateMode ? (v) => update(v) : (v) => upload(v))}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={navigateBack}>
                        <ArrowBackIcon
                            sx={{
                                color: ThemeColors.BLACK,
                                height: '24px',
                                width: '24px',
                            }}
                        />
                    </IconButton>
                    <Typography style={{ ...fonts.pageSubtitle, marginLeft: '16px' }} flexGrow={1}>
                        {updateMode
                            ? `${
                                  isUserAdmin ? 'Modifier' : 'Voir'
                              } une catégorie de reapprovisionnement`
                            : 'Ajouter une catégorie de reapprovisionnement'}
                    </Typography>

                    <Button
                        variant="outlined"
                        sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
                        onClick={navigateBack}
                    >
                        Annuler
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!isUserAdmin ? true : isUploading || isUpdating}
                    >
                        {updateMode ? 'Modifier' : 'Ajouter'}
                    </Button>
                </div>
                <LoadingPaper
                    sx={{ marginTop: '40px', padding: '24px', paddingBottom: '40px' }}
                    loading={isLoading && updateMode}
                >
                    <Typography mb="8px" style={fonts.inputHeader}>
                        Nom
                    </Typography>
                    <TextFieldRhf
                        variant="outlined"
                        label="Nom de la catégorie"
                        control={control}
                        trigger={trigger}
                        name="name"
                        fullWidth
                    />

                    <Typography mb="8px" mt="12px" style={fonts.inputHeader}>
                        J-x
                    </Typography>
                    <TextFieldRhf
                        variant="outlined"
                        label="J-x"
                        placeholder="2"
                        control={control}
                        trigger={trigger}
                        name="daysBefore"
                        fullWidth
                    />

                    <Typography mb="8px" mt="12px" style={fonts.inputHeader}>
                        Heure de calcul
                    </Typography>
                    <TimePickerRhf
                        label="Heure de calcul"
                        control={control}
                        trigger={trigger}
                        name="computeHour"
                    />

                    <Typography mb="8px" mt="12px" style={fonts.inputHeader}>
                        Heure d'envoi
                    </Typography>
                    <TimePickerRhf
                        label="Heure d'envoi"
                        control={control}
                        trigger={trigger}
                        name="sendHour"
                    />

                    <Typography mb="8px" mt="12px" style={fonts.inputHeader}>
                        Heure de log
                    </Typography>
                    <TimePickerRhf
                        label="Heure de log"
                        control={control}
                        trigger={trigger}
                        name="logHour"
                    />

                    <Typography mb="8px" mt="12px" style={fonts.inputHeader}>
                        Produits
                    </Typography>
                    <SelectMultipleRhf
                        options={productOptions}
                        control={control}
                        name="productIds"
                    />

                    {conflictingProducts.length > 0 && (
                        <>
                            <Typography
                                mt="8px"
                                style={{ ...fonts.informationSubText, color: ThemeColors.ORANGE }}
                            >
                                Attention : les produits ci-dessous sont déjà dans une autre
                                catégorie et seront déplacés.
                            </Typography>
                            <Typography
                                component="ul"
                                mt="6px"
                                sx={{
                                    ...fonts.informationSubText,
                                    color: ThemeColors.ORANGE,
                                    paddingLeft: '20px',
                                    margin: 0,
                                }}
                            >
                                {conflictingProducts.map((product) => (
                                    <li key={`${product.productLabel}-${product.categoryLabel}`}>
                                        {product.productLabel} — {product.categoryLabel}
                                    </li>
                                ))}
                            </Typography>
                        </>
                    )}
                </LoadingPaper>
            </form>
        </Page>
    );
};
